import { routesList } from "@/router";
import { RouteName } from "@/router/extra";
import stores from "@/store/index";
import Track from "@/utils/track";
import AKJS from "@akc/akjs";
import { ProductListItemModel, VideoCardScene } from "@akc/biz-api";
import { Pressable, PressableTargetType } from "@akc/decorate-ds";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Request } from "../api/request";
import { getAppSystem } from "./app-system";
import { isNewProductDetail } from "./gray";
import { MICRO_APP_BASEURL, platform } from "./platform";
import { getBaseurl, getUrlParam, urlQueryObject } from "./url-parse";
import { versionsCompare } from "./version-compare";

// 跳转到指定页面
export type NavigatorOptions = {
  url: string;
  query?: {
    [key in string]: any;
  };
};

const staticUrl = getBaseurl();

const track = Track();

const LinkType = {
  LINK: "链接",
  PRODUCT: "商品",
  ACTIVITY: "档期",
  SHOPMICRO: "搭建页面",
  HOTSALE_OLD: "超级会场",
  CONFERENCE_OLD: "普通会场",
  SHOP: "店铺S码",
  CONFERENCE_3: "会场3.0",
  PAGE: "搭建页面",
  LIVE_ROOM: "直播",
};

export type SearchPageParams = {
  from?: string;
  scene?: string;
  activityId?: string;
  activityType?: 2 | 3 | 4;
};

export interface Navigate2H5Options {
  replace?: boolean; //是否replace
  query?: string | Record<string, any>;
  // extra?: any; // 额外拓展参数，后期可能用做埋点
}

const getRewardAppSrc = (selectType: string, bodyIdList: string[]) => {
  if (selectType === "BASE_PRODUCT") {
    return "PRODUCT";
  }
  if (selectType === "ACTIVITY") {
    return bodyIdList.length > 1 ? "ACTIVITYLIST" : "ACTIVITY";
  }
  if (selectType === "SCHEDULE") {
    return "SCHEDULE";
  }
  if (selectType === "MERCHANT_SHOP" || selectType === "ALL") {
    return "SHOP";
  }
  if (selectType === "BRAND") {
    return "BRAND";
  }
};

function useNavigation() {
  const wx = window.wx;
  // 跳转主应用的 navigate
  const navigater = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const prePageName = encodeURIComponent(document.title) || "";
  /**
   * 将url和query参数对象拼接成url，自动添加shopId和distributorId
   * @param {string} url - url
   * @param {Object} params - query参数对象
   * @returns {string} - 拼接后的url
   */
  const stringifyUrl = (
    url: string,
    params: {
      [key in string]: any;
    },
  ) => {
    if (url) {
      let str = Object.keys(params)
        .filter(key => params[key] !== undefined)
        .map(key => `${key}=${params[key]}`)
        .join("&");
      const { shopId, distributorId, plt } = urlQueryObject();
      // 自动添加shopId
      const hasShopIdInUrl = params.shopId || url.includes("?shopId=") || url.includes("&shopId=");
      if (!hasShopIdInUrl) {
        str += (str ? "&" : "") + `shopId=${shopId}`;
      }
      const hasdistributorId =
        params.distributorId || url.includes("?distributorId=") || url.includes("&distributorId=");
      if (!hasdistributorId && distributorId) {
        str += `&distributorId=${distributorId}`;
      }
      const hasPreviousPageName =
        params.previous_page_name ||
        url.includes("?previous_page_name=") ||
        url.includes("&previous_page_name=");
      if (!hasPreviousPageName && prePageName) {
        str += `&previous_page_name=${prePageName}`;
      }
      if (plt) {
        str += `&plt=${plt}`;
      }
      const hyphen = url.includes("?") ? "&" : "?";
      return `${url}${hyphen}${str}`;
    }
    return "";
  };
  /**
   * 通用跳转方法
   * @param appOptions app跳转
   * @param miniOptions 小程序跳转
   * @param h5Options {url, query} h5跳转用，包括跳React和Vue页面
   * @param {string} h5Options.url 跳转的url
   * @param {Object} h5Options.query 跳转的query参数
   * @param type "outer" 外链跳转,
   * @param replace 是否replace
   */
  function navigate(options: {
    appOptions?: NavigatorOptions;
    miniOptions?: NavigatorOptions;
    h5Options?: NavigatorOptions;
    type?: "outer";
    replace?: boolean;
  }) {
    // 判断跳转前是什么平台
    const { isApp, isMini } = platform;
    if (isApp) {
      const { url, query } = options.appOptions || {};
      if (url) {
        const schema =
          url.indexOf("http") >= 0
            ? url
            : url.indexOf("akapp://") >= 0
              ? url
              : url.indexOf("/") >= 0
                ? `akapp://mengxiang.com${url}`
                : `akapp://mengxiang.com/${url}`;
        if (query) {
          query.page_name = encodeURIComponent(document.title);
        }
        AKJS.ready().then(() => {
          AKJS.action
            .dispatch("event.page.open", { url: schema, param: query ?? {} })
            .catch(error => {
              console.error("跳转失败:", error);
            });
        });
      }
    } else if (isMini) {
      const { url, query = {} } = options.miniOptions || {};
      if (url) {
        if (query) {
          query.page_name = encodeURIComponent(document.title);
        }
        let newurl = stringifyUrl(url, query);
        if (
          newurl.includes("/mx-shop-micro/") ||
          newurl.includes("mx-shop-micro?") ||
          newurl.includes("mx-shop-micro/?")
        ) {
          const hyphen = newurl.includes("?") ? "&" : "?";
          if (getUrlParam()?.["isC"]) {
            newurl = `${newurl + hyphen}token=${stores.appStore.token}&isC=${
              getUrlParam()?.["isC"]
            }`;
          } else {
            newurl = `${newurl + hyphen}token=${stores.appStore.token}`;
          }
          const url = `/pages/web-view/web-view?url=${encodeURIComponent(newurl)}`;
          if (searchParams.get("wxmpstack")) {
            // 小程序页面栈已满
            window.wx.miniProgram.redirectTo({
              url: url,
            });
          } else {
            window.wx.miniProgram.navigateTo({
              url: url,
            });
          }
          return;
        }
        if (newurl.startsWith("http")) {
          const hyphen = newurl.includes("?") ? "&" : "?";
          console.log("小程序跳转-----", window.location.href);
          if (getUrlParam()?.["isC"]) {
            // eslint-disable-next-line no-restricted-syntax
            window.location.href = `${newurl + hyphen}token=${
              stores.appStore.token
            }&isC=${getUrlParam()?.["isC"]}`;
          } else {
            // eslint-disable-next-line no-restricted-syntax
            window.location.href = `${newurl + hyphen}token=${stores.appStore.token}`;
          }
          return;
        }
        if (searchParams.get("wxmpstack")) {
          wx &&
            wx.miniProgram.redirectTo({
              url: newurl,
            });
        } else {
          wx &&
            wx.miniProgram.navigateTo({
              url: newurl,
            });
        }
      }
    } else {
      // 否则是H5
      const { url, query = {} } = options.h5Options || {};
      if (url) {
        if (query) {
          let name = "";
          if (query.promotePageType) {
            const pageNameObj = {
              1: "种草社区_推荐",
              2: "种草详情",
              3: "实拍专区-图文",
              4: "实拍专区-视频",
            };
            name = pageNameObj[query?.promotePageType];
            query.page_name = encodeURIComponent(name);
            query.previous_page_name = encodeURIComponent(name);
          } else {
            name = location.href.indexOf("mx-shop-micro/market/marketCategory")
              ? "店铺列表"
              : location.href.indexOf("mx-shop-micro/market/marketSearch")
                ? "搜索"
                : location.href.indexOf("mx-shop-micro/market")
                  ? "店铺聚合"
                  : "";
            const pageName = encodeURIComponent(document.title || (name ? name : "首页"));
            query.page_name = pageName;
            query.previous_page_name = pageName;
          }
        }
        // 将url和query参数对象拼接成url，自动添加shopId和distributorId
        let newUrl = stringifyUrl(url, query);
        if (newUrl) {
          let type = options.type;
          if (newUrl.indexOf("/#/productDetail") > -1 && isNewProductDetail()) {
            // 若是进入商详 && 在灰度中，进入新版商详
            type = undefined;
            newUrl = newUrl.replace("/#/productDetail", "/mx-shop-micro/product");
          }
          // 针对站外场景跳转
          if (type === "outer") {
            // eslint-disable-next-line no-restricted-syntax
            window.location.href = url;
            return;
          }
          // 微前端跳转 | 针对其他会场跳转
          navigater(newUrl, {
            state: query,
            replace: options.replace,
          });
        }
      }
    }
  }

  // 跳转更多素材
  const nav2RewardMaterialList = (info: any, brandIds: any) => {
    let { selectionProductType, bodyIdList } = info;
    bodyIdList = bodyIdList || [];
    brandIds = brandIds || [];
    const previous_page_name = `${encodeURIComponent(document.title)}`;

    const src = getRewardAppSrc(selectionProductType, bodyIdList);
    const appOpts: any = {
      src,
      bizInfo: {},
    };
    if (src === "SHOP") {
      if (bodyIdList?.length === 1) {
        appOpts.bizInfo.shopCode = bodyIdList[0];
      } else {
        appOpts.bizInfo.shopCodes = bodyIdList;
      }
    } else if (src === "SCHEDULE") {
      appOpts.bizInfo.scheduleIds = bodyIdList;
    } else if (src === "ACTIVITY") {
      appOpts.bizInfo.activityId = bodyIdList[0];
    } else if (src === "ACTIVITYLIST") {
      appOpts.bizInfo.activityIds = bodyIdList;
    } else if (src === "PRODUCT") {
      appOpts.bizInfo.fromRPromo = true;
      if (bodyIdList?.length === 1) {
        appOpts.bizInfo.activitySpuId = bodyIdList[0];
      } else {
        appOpts.bizInfo.productIds = bodyIdList;
      }
    } else if (src === "BRAND") {
      appOpts.bizInfo.brandIds = bodyIdList;
    }
    appOpts.bizInfo = JSON.stringify(appOpts.bizInfo);
    const opts: any = {
      selectionProductType,
      bodyIdList,
      brandIds,
      previous_page_name,
    };

    /* eslint-disable */
    setTimeout(() => {
      navigate({
        appOptions: {
          url: "materialMore",
          query: appOpts,
        },
        miniOptions: {
          url: "/material/pages/material-list-gray/material-list-gray",
          query: opts,
        },
        h5Options: {
          url: `/mx-shop-micro/material`,
          query: opts,
        },
      });
    }, 100);
  };

  // 跳转品牌素材
  const nav2RewardBrandMaterial = (query: {
    bodyIdList?: string[];
    selectionProductType?: string;
    brandIds?: string[];
  }) => {
    let { bodyIdList = [], selectionProductType = "", brandIds = [] } = query;
    bodyIdList = bodyIdList || [];
    brandIds = brandIds || [];
    const previous_page_name = `${encodeURIComponent(document.title)}`;
    const opts: any = {
      selectionProductType,
      bodyIdList,
      brandIds,
      previous_page_name,
    };
    const appOpts: any = {
      src: getRewardAppSrc(selectionProductType, bodyIdList),
      bizInfo: {},
    };
    if (bodyIdList?.length === 1) {
      appOpts.brandId = brandIds[0];
      appOpts.bizInfo.shopCode = bodyIdList[0];
    } else {
      appOpts.bizInfo.brandIds = brandIds;
      appOpts.bizInfo.shopCodes = bodyIdList;
    }
    appOpts.bizInfo = JSON.stringify(appOpts.bizInfo);
    /* eslint-disable */
    setTimeout(() => {
      navigate({
        appOptions: {
          url: "brandMaterial",
          query: appOpts,
        },
        miniOptions: {
          url: "/material/pages/brand-material/brand-material",
          query: opts,
        },
        h5Options: {
          url: `/mx-shop-micro/brand-material`,
          query: opts,
        },
      });
    }, 100);
  };

  // 跳转素材详情
  const nav2RewardMaterialContent = (query: { material: any }) => {
    const { material } = query;
    const { materialNo, circleId, activitySpuId, type } = material;
    console.log("material", material);
    const url =
      type === 1
        ? `${MICRO_APP_BASEURL}/#/circleMaterial?materialNo=${materialNo}&isCheckMaterialType=0&isKeepVisualAngle=1&activitySpuId=${activitySpuId}`
        : `${MICRO_APP_BASEURL}/#/materialInfo?materialNo=${materialNo}&isCheckMaterialType=0&isKeepVisualAngle=1&activitySpuId=${activitySpuId}`;
    const miniUrl =
      type === 1
        ? "/material/pages/circle-material/circle-material"
        : "/material/pages/material-info/material-info";

    // 圈/笔记
    const previous_page_name = `${encodeURIComponent(document.title)}`;
    /* eslint-disable */
    setTimeout(() => {
      navigate({
        appOptions: {
          url: type === 1 ? "circleDetail" : "materialDetail",
          query: {
            previous_page_name,
            materialId: materialNo,
            circleId,
            isFromMaterialUser: true,
            mAuditStatus: "2",
          },
        },
        miniOptions: {
          url: miniUrl,
          query: {
            previous_page_name,
            materialNo,
            activitySpuId,
          },
        },
        h5Options: {
          url,
          query: { previous_page_name, materialNo, activitySpuId },
        },
      });
    }, 100);
  };

  // 跳转到档期详情
  const navigate2Activity = (
    query: {
      activityNo: string;
      marketName?: string;
      promotePageType?: any; //种草/实拍专区跳转pagename
    },
    clickEvent?: any,
  ) => {
    /* eslint-disable */
    if (track.track && clickEvent) {
      if (clickEvent.btnInfo) {
        track.track("btn_click", {
          page_name: document.title,
          btn_name: clickEvent.btnInfo.btn_name,
          module: clickEvent.btnInfo.module,
          btn_text: clickEvent.btnInfo.btn_text,
          ...clickEvent,
        });
      } else {
        track.track("resource_click", {
          page_name: document.title,
          resource_type: clickEvent.type,
          resource_content: "档期",
          resource_name: "档期",
          resource_id: clickEvent?.targetId,
          resource_rank: clickEvent?.resourceRank,
          relative_rank: clickEvent?.relativeRank,
          has_picture: true,
          has_specially: clickEvent?.hasSpecially,
          activity_spu_code: clickEvent?.targetId,
          activity_id: query.activityNo,
          market_id: getUrlParam()?.["customPageCode"],
          market_name: document.title,
          ...clickEvent,
        });
      }
    }
    /* eslint-disable */
    setTimeout(() => {
      navigate({
        appOptions: {
          url: "activity",
          query: {
            id: query.activityNo,
            marketName: query.marketName,
          },
        },
        miniOptions: {
          url: "/acts/pages/activity-detail/activity-detail",
          query: {
            id: query.activityNo,
          },
        },
        h5Options: {
          url: `/mx-shop-micro/activity/${query.activityNo}`,
          query: {
            activityNo: query.activityNo,
            fromPage: query.marketName,
          },
        },
      });
    }, 100);
  };

  const navigate2LiveReview = (props: { liveId: string }) => {
    navigate({
      appOptions: {
        url: "liveVOD",
        query: {
          id: props.liveId,
        },
      },
      miniOptions: {
        url: `/liveGoods/livePlayback/pages/live-playback/live-playback?liveNo=${props.liveId}`,
      },
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/live-review?channel=h5Mall&liveNo=${props.liveId}`,
      },
    });
  };

  const navigate2LiveRoom = (props: { liveId: string; liveStatus?: number }) => {
    navigate({
      appOptions: {
        url: "liveRoom",
        query: {
          id: props.liveId,
        },
      },
      miniOptions: {
        url: `/liveGoods/livePlaying/pages/live-playing/live-playing?liveNo=${props.liveId}`,
      },
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/living?channel=h5Mall&liveNo=${props.liveId}`,
      },
    });
  };

  const navigate2PreheatLiveRoom = (props: { liveId: string; liveStatus?: number }) => {
    navigate({
      appOptions: {
        url: "live_trailer",
        query: {
          id: props.liveId,
        },
      },
      miniOptions: {
        url: `/liveGoods/livePreheat/pages/live-preheat/live-preheat?liveNo=${props.liveId}`,
      },
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/live-preheat?channel=h5Mall&liveNo=${props.liveId}`,
      },
    });
  };

  const navigate2LiveItem = (liveData: any) => {
    const { liveStatus = 10, liveNo: liveId = "", playBackUrl = "" } = liveData || {};
    if (liveStatus === 10) {
      navigate2PreheatLiveRoom({
        liveId,
      });
    } else if (liveStatus === 20) {
      navigate2LiveRoom({
        liveId,
      });
    } else if (liveStatus > 20 && playBackUrl) {
      navigate2LiveReview({
        liveId,
      });
    }
  };

  const navigate2HiComi = query => {
    // unmountApp("h5shop", {
    //   destroy: true,
    // });
    navigate({
      miniOptions: {
        url: `${origin}/mx-shop-micro/decorate/profit`,
        query: {
          merchantShopCode: query.merchantShopCode,
        },
      },
      h5Options: {
        url: `/mx-shop-micro/decorate/profit`,
        query: {
          merchantShopCode: query.merchantShopCode,
        },
      },
    });
  };
  // 跳转到首页
  const navigate2Home = () => {
    const { isApp, isMini, isWxH5, isH5 } = platform;
    if (isApp) {
      navigate({
        appOptions: {
          url: "home",
        },
      });
    } else if (isWxH5 || isH5) {
      navigate2H5(RouteName.Home);
    } else if (isMini) {
      window?.wx?.miniProgram?.reLaunch({
        url: "/pages/home/home",
      });
    }
  };
  // 跳转到商品详情
  const navigate2Product = async (
    query: (
      | { activitySpuId: string; productId?: never; forceNormalProduct?: boolean }
      | { activitySpuId?: never; productId: string; forceNormalProduct?: boolean }
    ) & {
      activityId?: string;
      firstSalePropertyValue?: string;
      selectSkuId?: string;
      marketName?: string;
      promotePageType?: any; //种草实拍专区pagename
      forceNormalProduct?: boolean;
      liveNo?: string;
    },
    clickEvent?: any,
    replace?: boolean,
  ) => {
    /* eslint-disable */
    if (track.track && clickEvent) {
      if (clickEvent.btnInfo) {
        track.track("btn_click", {
          page_name: document.title,
          btn_name: clickEvent.btnInfo.btn_name,
          module: clickEvent.btnInfo.module,
          btn_text: clickEvent.btnInfo.btn_text,
          ...clickEvent,
        });
      } else {
        track.track("resource_click", {
          page_name: document.title,
          resource_type: clickEvent.type,
          resource_content: "商品",
          resource_name: "商品",
          resource_id: clickEvent?.targetId,
          resource_rank: clickEvent?.resourceRank,
          relative_rank: clickEvent?.relativeRank,
          has_picture: true,
          has_specially: clickEvent?.hasSpecially,
          activity_spu_code: clickEvent?.targetId,
          market_id: getUrlParam()?.["customPageCode"],
          market_name: document.title,
          ...clickEvent,
        });
      }
    }
    /* eslint-enable */
    const { isApp } = platform;
    if (isApp) {
      const appVersion = (await getAppSystem())?.appVersion;
      if (appVersion && versionsCompare(appVersion, "7.0.0") < 0) {
        // 老版本，进入档期详情并定位到商品
        const response = await Request({
          url: `${staticUrl}/api/mshop/mshop-aggr-prod/outer/product/convertProductId`,
          method: "post",
          data: {
            activitySpuIds: [query.activitySpuId],
            convertType: 2,
          },
        });
        if (response?.data && (response?.data?.length ?? 0) > 0) {
          const skcId = response?.data[0]?.skcId;
          if (!skcId) {
            console.log(`未查到对应的skcid. activitySpuId: ${query.activitySpuId}`);
            return;
          }
          navigate({
            appOptions: {
              url: "activity",
              query: {
                id: query.activityId,
                productId: skcId,
              },
            },
          });
        }
        return;
      }
    }
    setTimeout(() => {
      navigate({
        appOptions: {
          url: "productDetail",
          query: {
            activityId: query.activityId,
            productId: query.activitySpuId,
            selectSkuId: query.firstSalePropertyValue ? undefined : query.selectSkuId,
            firstSalePropertyValue: query.firstSalePropertyValue,
            liveNo: query.liveNo,
          },
        },
        miniOptions: {
          url: "/acts/pages/product-detail/product-detail?activitySpuId",
          query: {
            activityId: query.activityId,
            activitySpuId: query.activitySpuId,
            selectSkuId: query.firstSalePropertyValue ? undefined : query.selectSkuId,
            firstSalePropertyValue: query.firstSalePropertyValue,
            token: token,
            liveNo: query.liveNo,
          },
        },
        h5Options: {
          url: `${MICRO_APP_BASEURL}/#/productDetail`,
          query: {
            activityId: query.activityId,
            activitySpuId: query.activitySpuId,
            selectSkuId: query.firstSalePropertyValue ? undefined : query.selectSkuId,
            firstSalePropertyValue: query.firstSalePropertyValue,
            promotePageType: query.promotePageType, //种草实拍专区pagename
            forceNormalProduct: query.forceNormalProduct,
            liveNo: query.liveNo,
          },
        },
        replace: replace,
      });
    }, 100);
  };

  /**
   * 跳转到商品视频大卡
   * @param query
   * @param replace
   * @param hideForwardBtn 是否隐藏转发按钮，1为隐藏，0为不隐藏
   */
  const navigate2ProductVideoStream = (
    query: {
      activityId?: string;
      activitySpuId: string;
      vcScene?: string;
      selectSkuId?: string;
      firstSalePropertyValue?: string;
      showVideoBtmGuide?: string;
      showVideoBottomBar?: string;
      hideForwardBtn?: string;
      theme?: string;
      materialNo?: string;
      liveNo?: string;
    },
    replace = false,
  ) => {
    navigate({
      h5Options: {
        url: `${MICRO_APP_BASEURL}/mx-shop-micro/video-card-list`,
        query: {
          activityId: query.activityId,
          activitySpuId: query.activitySpuId,
          vcScene: query.vcScene || VideoCardScene.videoCardOtherScene,
          showVideoBtmGuide: query.showVideoBtmGuide,
          showVideoBottomBar: query.showVideoBottomBar,
          hideForwardBtn: query.hideForwardBtn,
          theme: query.theme,
          materialNo: query.materialNo || "",
          liveNo: query.liveNo || "",
        },
      },
      miniOptions: {
        url: "/acts/pages/product-detail/video-card-list/video-card-list",
        query: {
          activityId: query.activityId,
          activitySpuId: query.activitySpuId,
          vcScene: query.vcScene || VideoCardScene.videoCardOtherScene,
          showVideoBtmGuide: query.showVideoBtmGuide || "0",
          showVideoBottomBar: query.showVideoBottomBar || "0",
          hideForwardBtn: query.hideForwardBtn || "0",
          theme: query.theme || "dark",
          materialNo: query.materialNo || "",
          liveNo: query.liveNo || "",
        },
      },
      appOptions: {
        url: "productDetail",
        query: {
          activityId: query.activityId,
          productId: query.activitySpuId,
          selectSkuId: query.firstSalePropertyValue ? undefined : query.selectSkuId,
          firstSalePropertyValue: query.firstSalePropertyValue,
          isSkipProductVideoStream: true,
          liveNo: query.liveNo || "",
        },
      },
      replace,
    });
  };

  /**
   * 跳转到素材品视频大卡
   * @param query
   * @param replace
   */
  const navigate2MaterialVideoStream = (
    query: {
      materialNo?: string;
      spuId?: string; //基础商品id,有些页面可能叫baseProductIds
      activitySpuId?: string; //活动商品ID，有些页面可能叫objectIds
      queryType?: string;
      [key: string]: string | undefined; //可以传shopId之类
    },
    replace = false,
  ) => {
    navigate({
      h5Options: {
        url: `${MICRO_APP_BASEURL}/mx-shop-micro/material-video-list`,
        query: {
          materialNo: query.materialNo,
          spuId: query.spuId,
          activitySpuId: query.activitySpuId,
          queryType: query.queryType,
          ...(query || {}),
        },
      },
      replace,
    });
  };

  // 跳转到小程序积分券详情
  const navigate2SaasCoupon = async (query: { awdId: string; promoActivityId: string }) => {
    /* eslint-disable */
    if (track.track) {
      track.track("btn_click", {
        page_name: document.title,
        btn_name: "去兑换",
        module: "积分优惠券",
        btn_text: "去兑换",
      });
    }
    /* eslint-enable */
    setTimeout(() => {
      navigate({
        miniOptions: {
          url: "/jifen/pages/exchange/exchange",
          query: {
            awdId: query.awdId,
            promoActivityId: query.promoActivityId,
          },
        },
      });
    }, 100);
  };

  //跳购物车
  const navigate2Cart = async () => {
    setTimeout(() => {
      navigate({
        h5Options: {
          url: `${MICRO_APP_BASEURL}/#/cart`,
        },
        miniOptions: {
          url: "/pages/cart/cart",
        },
      });
    }, 100);
  };

  // 跳转到优惠券档期凑单页
  function navigate2OfferOrderList(query: any) {
    navigate({
      miniOptions: {
        url: "/acts/pages/offer-order/offer-order",
        query: query.miniQuery,
      },
      h5Options: {
        // url: `${MICRO_APP_BASEURL}/#/offer-order`,
        url: "/mx-shop-micro/joint-bill",
        query: query.h5Query,
      },
    });
  }

  // 跳转到优惠券商品凑单页
  function navigate2GoodsOfNpieceXfold(query: any) {
    navigate({
      miniOptions: {
        url: "/acts/pages/offer-order/offer-order",
        query: query.miniQuery,
      },
      h5Options: {
        // url: `${MICRO_APP_BASEURL}/#/goodsOfNpieceXfold`,
        url: "/mx-shop-micro/joint-bill",
        query: query.h5Query,
      },
    });
  }

  // 跳转到普通会场详情
  function navigate2Venue(query: {
    miniQuery?: {
      id: string;
    };
    h5Query?: {
      conferenceId: string;
    };
  }): void {
    navigate({
      appOptions: {
        url: `${process.env.REACT_APP_PAGE_URL_4_APP}/pages/commonActivity.html#/index?conferenceId=${query.h5Query?.conferenceId}`,
      },
      miniOptions: {
        url: "/acts/pages/conference/conference",
        query: query.miniQuery,
      },
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/commonActivity?conferenceId=${query.h5Query?.conferenceId}`,
      },
    });
  }

  // 跳转到超品会场详情
  function navigate2SuperVenue(query: {
    miniQuery?: {
      pageName?: string;
      shareOnlyId: string;
    };
    h5Query?: {
      hotSaleId: string;
    };
  }) {
    navigate({
      appOptions: {
        url: `${process.env.REACT_APP_PAGE_URL_4_APP}/pages/superBrand.html#/index?hotSaleId=${query.h5Query?.hotSaleId}`,
      },
      miniOptions: {
        url: "/pages/transfer/transfer",
        query: query.miniQuery,
      },
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/superbrand?hotSaleId=${query.h5Query?.hotSaleId}`,
      },
    });
  }

  //跳转到小程序搜索页
  function navigate2Search(query: {
    miniQuery?: {
      merchantShopCode?: any;
      shopId?: any;
      scene?: any;
    };
    h5Query?: {
      merchantShopCode: any;
      shopId?: any;
      scene?: any;
    };
  }) {
    navigate({
      miniOptions: {
        url: "/acts/pages/mix-search/mix-search",
        query: query.miniQuery,
      },
      h5Options: {
        url: `/mx-shop-micro/search?merchantShopCode=${query.h5Query?.merchantShopCode}&scene=${query.h5Query?.scene}&timestamp=${Date.now()}`,
      },
    });
  }

  //跳转到店铺资质信息页面
  function navigate2Shop(query: { shopCode: any }) {
    navigate({
      miniOptions: {
        url: `${location.origin}/mx-shop-micro/shop/info`,
        query: query,
      },
      h5Options: {
        url: "/mx-shop-micro/shop/info",
        query: query,
      },
    });
  }

  //跳转到店铺详情页面
  function navigate2Store(query) {
    navigate({
      appOptions: {
        url: "shopDetail",
        query,
      },
      miniOptions: {
        url: "/shop/pages/market/index",
        query,
      },
      h5Options: {
        url: "/mx-shop-micro/decorate",
        query,
      },
    });
  }

  //跳转到分类下级页面
  function navigate2Category(query: {
    shopCode?: string | null;
    categoryId?: string;
    categoryName?: string;
    threeClassId: string;
  }) {
    let h5Url = `${MICRO_APP_BASEURL}/#/categoryDetail?threeClassId=${query.threeClassId}`;
    if (query.categoryName) {
      h5Url += `&categoryName=${query.categoryName}`;
    }
    if (query.categoryId) {
      h5Url += `&categoryId=${query.categoryId}`;
    }
    if (query.shopCode) {
      h5Url += `&shopCode=${query.shopCode}`;
    }
    h5Url += "&refresh=true";
    navigate({
      miniOptions: {
        url: "/acts/pages/category-detail/category-detail",
        query,
      },
      h5Options: {
        url: h5Url,
      },
    });
  }

  // 跳转到确认订单页面
  function navigate2OrderConfrim(query: {
    miniQuery?: {
      skuNo: any;
      quantity: any;
      virtualRechargeType: any;
      activityNo: any;
      spuId;
      //是否作为礼品购买
      isGift?: number;
      hasRwdGift?: number | string;
    };
    h5Query?: {
      skuNo: any;
      num: any;
      directcChargeType: any;
      activityNo: any;
      spuId: any;
      liveRoomNo?: any;
      isBusinessVenue?: any;
      //是否作为礼品购买
      isGift?: number;
      hasRwdGift?: number | string;
    };
  }) {
    navigate({
      miniOptions: {
        url: "/score/pages/order-confirm/order-confirm",
        query: query.miniQuery,
      },
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/confirmationOrder?skuNo=${query.h5Query?.skuNo}&num=${query.h5Query?.num}&directcChargeType=${query.h5Query?.directcChargeType}&activityNo=${query.h5Query?.activityNo}&spuId=${query.h5Query?.spuId}&liveRoomNo=${query.h5Query?.liveRoomNo}&isBusinessVenue=${query.h5Query?.isBusinessVenue}&channel=h5Mall&isGift=${query.h5Query?.isGift}&hasRwdGift=${query.h5Query?.hasRwdGift}`,
      },
    });
  }

  /**
   * 点击 Pressable 动作
   */
  function preasableAction(
    item: Pressable,
    pageName?: string,
    type?: string,
    resourceRank?: string,
    relativeRank?: string,
    trackInfo?: any,
  ) {
    /* eslint-disable */
    if (track.track) {
      track.track("resource_click", {
        page_name: document.title,
        resource_type: type,
        resource_content: item?.targetType ? LinkType[item?.targetType] : item?.targetType,
        resource_name: "",
        resource_id: item?.targetId,
        resource_rank: resourceRank,
        relative_rank: relativeRank,
        has_picture: true,
        has_specially: false,
        market_id: getUrlParam()?.["customPageCode"],
        market_name: document.title,
        ...trackInfo,
      });
    }
    /* eslint-enable */
    if (!item?.targetId) {
      return;
    }
    const origin = location.origin;
    console.log("preasableAction——", item);
    console.log("%cPressableTargetType", "color: green;", JSON.stringify(PressableTargetType));
    switch (item.targetType) {
      case PressableTargetType.link: {
        // // 链接
        // const options = {
        //   url: item.targetId,
        // };
        // navigate({
        //   appOptions: options,
        //   miniOptions: options,
        //   h5Options: options,
        //   type: "outer",
        // });
        const options = {
          url: `${item.targetId.split("?")[0]}`,
          query: getUrlParam(null, item.targetId || "") || {},
        };
        console.log("小程序端跳转参数——", options);
        const isOuterLink = !item.targetId.includes("/mx-shop-micro");
        let path = "";
        if (!isOuterLink) {
          const start = item.targetId.indexOf("/mx-shop-micro");
          const end =
            item.targetId.indexOf("?") === -1 ? item.targetId.length : item.targetId.indexOf("?");
          path = item.targetId.substring(start, end);
        }
        console.log("h5端跳转参数——", {
          isOuterLink,
          path,
          query: options.query,
        });
        navigate({
          appOptions: {
            url: item.targetId,
          },
          miniOptions: options,
          h5Options: {
            url: isOuterLink ? item.targetId : path,
            query: options.query,
          },
          type: isOuterLink ? "outer" : undefined,
        });
        break;
      }
      case PressableTargetType.product: {
        // 商品
        if (!item.targetId) {
          return;
        }
        navigate2Product({
          activityId: item.activityId,
          activitySpuId: item.targetId,
          marketName: pageName,
        });
        break;
      }
      case PressableTargetType.activity: {
        // 档期
        navigate2Activity({
          activityNo: item.targetId,
          marketName: pageName,
        });
        break;
      }
      case "CONFERENCE_3" as PressableTargetType:
      case PressableTargetType.page:
      case PressableTargetType.shopMicro: {
        // 搭建页面
        const url = `${location.href.split("?")[0]}`;
        const options = {
          url,
          query: {
            customPageCode: item.targetId,
            merchantShopCode: getUrlParam()?.["merchantShopCode"],
          },
        };
        if (location.href.indexOf("/decorate") >= 0) {
          navigate({
            appOptions: {
              url: `${url}?customPageCode=${item.targetId}`,
            },
            miniOptions: options,
            h5Options: {
              url: `/mx-shop-micro?customPageCode=${
                item.targetId
              }&merchantShopCode=${getUrlParam()?.["merchantShopCode"]}`,
            },
          });
        } else {
          navigate({
            appOptions: {
              url: `${url}?customPageCode=${item.targetId}`,
            },
            miniOptions: options,
            h5Options: {
              url: `/mx-shop-micro?customPageCode=${item.targetId}`,
            },
          });
        }

        sessionStorage.setItem("conference3_share_flag", "true");
        break;
      }
      case PressableTargetType.saasShopMicro: {
        navigate({
          miniOptions: {
            url: "/pages/home-saas-sec/preview",
            query: { customPageCode: item.targetId },
          },
        });
        break;
      }
      case PressableTargetType.hotSaleOld: {
        // 超品会场-老
        navigate2SuperVenue({
          miniQuery: { pageName: "hotSale", shareOnlyId: item.targetId },
          h5Query: { hotSaleId: item.targetId },
        });
        break;
      }
      case PressableTargetType.conferenceOld: {
        // 普通会场-老
        navigate2Venue({
          miniQuery: { id: item.targetId },
          h5Query: { conferenceId: item.targetId },
        });
        break;
      }
      case PressableTargetType.live: {
        // 直播间
        navigate2LiveRoom({ liveId: item.targetId });
        break;
      }
      case PressableTargetType.shop: {
        // 店铺页面
        navigate({
          appOptions: {
            url: "shopDetail",
            query: {
              shopCode: item.targetId,
            },
          },
          miniOptions: {
            url: `${origin}/mx-shop-micro/decorate`,
            query: {
              merchantShopCode: item.targetId,
            },
          },
          h5Options: {
            url: `/mx-shop-micro/decorate?merchantShopCode=${item.targetId}`,
          },
        });
        break;
      }
      case PressableTargetType.seckillList: {
        // 入秒杀二级页
        const distributorId = getUrlParam()?.["distributorId"];
        // unmountApp("h5shop", {
        //   destroy: true,
        // });
        const url = location.href.split("mx-shop-micro");
        let query = `mx-shop-micro/decorate/seckill?merchantShopCode=${
          getUrlParam()?.["merchantShopCode"]
        }&decorate=1&activeIndex=1&shopId=${getUrlParam()?.["shopId"]}&isC=${
          getUrlParam()?.["isC"]
        }&previous_page_name=店铺首页&token=${getUrlParam()?.["token"]}`;
        if (getUrlParam()?.["preview"] === true || getUrlParam()?.["preview"] === "true") {
          query += "&preview=true";
        }
        if (trackInfo?.page_code) {
          query += `&customPageCode=${trackInfo?.page_code}`;
        }
        if (trackInfo?.page_version) {
          query += `&customPageVersion=${trackInfo?.page_version}`;
        }
        if (distributorId) {
          if (!platform.isMini) {
            navigater(`/${query}&distributorId=${distributorId}`);
          } else {
            const newUrl = `${url[0]}${query}&distributorId=${distributorId}`;
            window.wx.miniProgram.navigateTo({
              url: `/pages/web-view/web-view?url=${encodeURIComponent(newUrl)}`,
            });
          }
        } else {
          if (!platform.isMini) {
            navigater(`/${query}`);
          } else {
            const newUrl = `${url[0]}${query}`;
            window.wx.miniProgram.navigateTo({
              url: `/pages/web-view/web-view?url=${encodeURIComponent(newUrl)}`,
            });
          }
        }
        break;
      }
      case PressableTargetType.profitAdditionalList: {
        // 进入高佣二级页
        const distributorId = getUrlParam()?.["distributorId"];
        // unmountApp("h5shop", {
        //   destroy: true,
        // });
        const url = location.href.split("mx-shop-micro");
        let query = `mx-shop-micro/decorate/profit?merchantShopCode=${
          getUrlParam()?.["merchantShopCode"]
        }&type=image&shopId=${getUrlParam()?.["shopId"]}&isC=${
          getUrlParam()?.["isC"]
        }&previous_page_name=店铺首页&token=${getUrlParam()?.["token"]}`;
        if (getUrlParam()?.["preview"] === true || getUrlParam()?.["preview"] === "true") {
          query += "&preview=true";
        }
        if (distributorId) {
          if (!platform.isMini) {
            navigater(`/${query}&distributorId=${distributorId}`);
          } else {
            const newUrl = `${url[0]}${query}&distributorId=${distributorId}`;
            window.wx.miniProgram.navigateTo({
              url: `/pages/web-view/web-view?url=${encodeURIComponent(newUrl)}`,
            });
          }
        } else {
          if (!platform.isMini) {
            navigater(`/${query}`);
          } else {
            const newUrl = `${url[0]}${query}`;
            window.wx.miniProgram.navigateTo({
              url: `/pages/web-view/web-view?url=${encodeURIComponent(newUrl)}`,
            });
          }
        }
        break;
      }
      case PressableTargetType.toplist: {
        // 进入榜单二级页
        const distributorId = getUrlParam()?.["distributorId"];
        // unmountApp("h5shop", {
        //   destroy: true,
        // });
        const url = location.href.split("mx-shop-micro");
        let query = `mx-shop-micro/decorate/toplist?merchantShopCode=${
          getUrlParam()?.["merchantShopCode"]
        }&customPageCode=${getUrlParam()?.["customPageCode"]}&shopId=${
          getUrlParam()?.["shopId"]
        }&isC=${getUrlParam()?.["isC"]}&previous_page_name=店铺首页&token=${
          getUrlParam()?.["token"]
        }`;
        if (getUrlParam()?.["preview"] === true || getUrlParam()?.["preview"] === "true") {
          query += "&preview=true";
        }
        if (distributorId) {
          if (!platform.isMini) {
            navigater(`/${query}&distributorId=${distributorId}`);
          } else {
            const newUrl = `${url[0]}${query}&distributorId=${distributorId}`;
            window.wx.miniProgram.navigateTo({
              url: `/pages/web-view/web-view?url=${encodeURIComponent(newUrl)}`,
            });
          }
        } else {
          if (!platform.isMini) {
            navigater(`/${query}`);
          } else {
            const newUrl = `${url[0]}${query}`;
            window.wx.miniProgram.navigateTo({
              url: `/pages/web-view/web-view?url=${encodeURIComponent(newUrl)}`,
            });
          }
        }
        break;
      }
      case PressableTargetType.couponList: {
        // 进入优惠券二级页
        const distributorId = getUrlParam()?.["distributorId"];
        const customPageCode = getUrlParam()?.["customPageCode"];
        // unmountApp("h5shop", {
        //   destroy: true,
        // });
        const url = location.href.split("mx-shop-micro");
        let query = `mx-shop-micro/decorate/coupon?merchantShopCode=${
          getUrlParam()?.["merchantShopCode"]
        }&shopId=${
          getUrlParam()?.["shopId"]
        }&customPageCode=${customPageCode}&previous_page_name=店铺首页&token=${
          getUrlParam()?.["token"]
        }`;
        if (getUrlParam()?.["preview"] === true || getUrlParam()?.["preview"] === "true") {
          query += "&preview=true";
        }
        if (distributorId) {
          if (!platform.isMini) {
            navigater(`/${query}&distributorId=${distributorId}`);
          } else {
            const newUrl = `${url[0]}${query}&distributorId=${distributorId}`;
            window.wx.miniProgram.navigateTo({
              url: `/pages/web-view/web-view?url=${encodeURIComponent(newUrl)}`,
            });
          }
        } else {
          if (!platform.isMini) {
            navigater(`/${query}`);
          } else {
            const newUrl = `${url[0]}${query}`;
            window.wx.miniProgram.navigateTo({
              url: `/pages/web-view/web-view?url=${encodeURIComponent(newUrl)}`,
            });
          }
        }
        break;
      }
      case PressableTargetType.mini: {
        const path = item.targetId || {};
        if (path) {
          window.wx.miniProgram.navigateTo({
            url: `/${path}`,
          });
        }
        // const APPID = window.wx.miniProgram.getAccountInfoSync().miniProgram.appId;
        // const path = item.targetId || {};
        // const appId = item.miniAppId;
        // if (APPID === item.miniAppId) {
        //   if (path) {
        //     window.wx.miniProgram.navigateTo({
        //       url: `/${path}`,
        //     });
        //   }
        // } else {
        //   window.wx.miniProgram.navigateToMiniProgram({
        //     appId,
        //     path,
        //     extraData: {
        //       currentSellerId:
        //         window.wx.miniProgram.getStorageSync("userInfo") &&
        //         window.wx.miniProgram.getStorageSync("userInfo").currentSellerId,
        //     },
        //   });
        // }
        break;
      }
    }
  }

  /**
   * 拼接路由名称和查询参数
   * @param {string} urlString 路由路径path
   * @param {Record<string, any> | string} query 查询参数
   */
  const appendQuery = (urlString: string, query?: Record<string, any> | string) => {
    // query已经是字符串形式，直接拼接
    if (typeof query === "string") {
      urlString = urlString + (urlString.indexOf("?") > -1 ? "&" : "?") + query;
    }

    const hasParam = (key: string) => {
      return urlString.includes(`?${key}=`) || urlString.includes(`&${key}=`);
    };

    // 补充拼接shopId、distributorId、token等公共参数
    const nQuery = typeof query === "string" ? {} : (query as Record<string, any>);
    const { isMini } = platform;
    const { shopId, distributorId } = urlQueryObject();
    const queries = {
      ...nQuery,
      ...(hasParam("shopId") ? {} : { shopId }),
      ...(hasParam("distributorId") ? {} : { distributorId }),
      ...(isMini ? { token: token } : {}),
      channel: "h5Mall",
      previous_page_name: encodeURIComponent(document.title) || "",
    };
    let res = Object.keys(queries)
      .map(key => queries[key] && key + "=" + queries[key])
      .filter(i => i)
      .join("&");

    return urlString + (urlString.indexOf("?") > -1 ? "&" : "?") + res;
  };

  /**
   * 跳转到H5页面，包括主应用和微应用页面
   * @param {string | RouteName} urlString 路由名称name或路由路径path，如果是微应用，要传"/#/top-list"形式
   * @param {Navigate2H5Options} options 跳转参数
   * @param {Record<string, any>} options.query 跳转参数
   * @param {boolean} options.replace 是否替换当前页面
   */
  const navigate2H5 = (urlString: string | RouteName, options?: Navigate2H5Options) => {
    // 如果urlString是路由名称name，则转换为路由路径path
    if (Object.values(RouteName).includes(urlString as RouteName)) {
      urlString = routesList.find(item => item.name === urlString)?.path || "";
    }
    // 拼接路由名称和查询参数
    const u = appendQuery(urlString, options?.query);
    console.log("navigate2H5", u);
    if (u.indexOf("/mx-shop-micro") > -1) {
      // 如果是主应用URL，使用navigate跳转
      const path = u.substring(u.indexOf("/mx-shop-micro"));
      navigater(path, {
        replace: options?.replace ?? false,
      });
    } else {
      // 否则是微应用URL，使用window.location.href跳转
      // eslint-disable-next-line no-restricted-syntax
      window.location.href = u;
    }
  };

  const navigateBack = (delta = -1) => {
    if (window.history.length === 1) {
      navigate2Home();
    } else {
      navigater(delta);
    }
  };

  // 跳转到优惠券列表页
  const navigate2CouponList = () => {
    navigate({
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/coupon`,
      },
    });
  };

  // 跳转到素材内容
  const navigate2MaterialContent = (type, materialNo) => {
    const url =
      type === 1
        ? `${MICRO_APP_BASEURL}/#/circleMaterial?materialNo=${materialNo}&isCheckMaterialType=1&isKeepVisualAngle=1`
        : `${MICRO_APP_BASEURL}/#/materialInfo?materialNo=${materialNo}&isCheckMaterialType=1&isKeepVisualAngle=1`;
    navigate({
      h5Options: {
        url: url,
      },
    });
  };

  // 跳转到素材列表
  const navigate2MaterialList = (
    merchantShopCode?: string,
    resourceCode?: string,
    activityIds?: string[],
  ) => {
    const params: string[] = [];
    if (merchantShopCode) {
      params.push(`merchantShopCode=${merchantShopCode}`);
    }
    if (resourceCode) {
      params.push(`resourceCode=${resourceCode}`);
    }
    if (activityIds) {
      params.push(`activityNo=${activityIds.join(",")}`);
      params.push(`previous_page_name=${encodeURIComponent(document.title)}`);
    }
    let url = `/mx-shop-micro/material?${params.join("&")}`;
    navigate({
      h5Options: {
        url: url,
      },
    });
  };
  // 跳转到品牌素材列表页
  const navigate2BrandMaterialList = (brandId: string) => {
    const url = `/mx-shop-micro/brand-material?brandId=${brandId}&previous_page_name=${encodeURIComponent(document.title)}`;
    navigate({
      h5Options: {
        url: url,
      },
    });
  };

  // 跳素材详情
  const navigate2Material = (type, materialNo, activitySpuId) => {
    const url =
      type === 1
        ? `${MICRO_APP_BASEURL}/#/circleMaterial?materialNo=${materialNo}&isCheckMaterialType=0&isKeepVisualAngle=1&activitySpuId=${activitySpuId}`
        : `${MICRO_APP_BASEURL}/#/materialInfo?materialNo=${materialNo}&isCheckMaterialType=0&isKeepVisualAngle=1&activitySpuId=${activitySpuId}`;
    const miniUrl =
      type === 1
        ? "/material/pages/circle-material/circle-material"
        : "/material/pages/material-info/material-info";
    navigate({
      h5Options: {
        url,
      },
      miniOptions: {
        url: miniUrl,
        query: {
          isKeepVisualAngle: 0,
          materialNo,
          activitySpuId,
        },
      },
      appOptions: {
        url: type === 1 ? "circleDetail" : "materialDetail",
        query: {
          materialId: materialNo,
          circleId: materialNo,
          isFromMaterialUser: true,
          mAuditStatus: "2",
        },
      },
    });
  };

  // 跳转到品牌详情
  const navigate2BrandDetail = (brandId: string) => {
    navigate({
      appOptions: {
        url: "brandDetail",
        query: {
          brandId,
        },
      },
      miniOptions: {
        url: "/acts/pages/brand-detail/brand-detail",
        query: {
          brandId,
        },
      },
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/brandDetail?brandId=${brandId}`,
      },
    });
  };

  // 跳转到品牌故事
  const navigate2BrandStory = (activityId: string) => {
    navigate({
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/brandStory?activityId=${activityId}`,
      },
    });
  };

  // 跳转到C品牌故事
  const navigate2CBrandStory = (activityId: string) => {
    navigate({
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/officialMaterial?channel=h5Mall&activityNo=${activityId}&materialChannel=1&shopBizCode=${
          getUrlParam()?.["shopId"]
        }`,
      },
    });
  };

  // 跳转到搜索页面
  const navigate2SearchPage = (params?: SearchPageParams) => {
    let sParams = params
      ? Object.getOwnPropertyNames(params)
          .filter(key => params[key] !== undefined)
          .map(key => `${key}=${params[key]}`)
          .join("&")
      : "";
    if (sParams) {
      sParams = `?${sParams}&timestamp=${Date.now()}`;
    } else {
      sParams = `?timestamp=${Date.now()}`;
    }

    navigate({
      h5Options: {
        // url: `${MICRO_APP_BASEURL}/#/sinSearch${sParams}`,
        url: `/mx-shop-micro/search${sParams}`,
      },
    });
  };

  // 跳转营业执照页面
  const navigate2BusinessLicense = (activityId: string) => {
    navigate({
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/businessLicense?externalActivityCode=${activityId}&shopId=${
          getUrlParam()?.["shopId"]
        }`,
      },
    });
  };

  // 跳转营销NX商品凑单页面
  const navigate2NxProductList = (promoActivityIds: string | string[]) => {
    navigate({
      h5Options: {
        url: `/mx-shop-micro/join-order-nx?marketingId=${Array.isArray(promoActivityIds) ? promoActivityIds.join(",") : promoActivityIds}`,
      },
    });
  };

  // 跳转直播页面
  const navigate2Live = (liveNo: string, liveStatus?: number) => {
    const url = `${MICRO_APP_BASEURL}/#/${
      liveStatus === 10 ? "live-preheat" : liveStatus === 20 ? "living" : "live-end"
    }?liveNo=${liveNo}`;
    navigate({
      h5Options: {
        url: url,
      },
    });
  };

  // 跳转新人券落地页（旧）
  const navigate2NewPersonPage = (configId: string) => {
    navigate({
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/newPersonPage?configId=${configId}&shopId=${
          getUrlParam()?.["shopId"]
        }`,
      },
    });
  };

  // 我的页面item跳转
  const navigate2MyItem = (itemPath: string, itemQuery?: string) => {
    const shopId = getUrlParam()?.["shopId"] ?? "";
    const distributorId = getUrlParam()?.["distributorId"] ?? "";
    const pathQuery = `shopId=${shopId}${distributorId ? "&" + distributorId : ""}`;
    navigate({
      h5Options: {
        url: `${MICRO_APP_BASEURL}${itemPath}?${pathQuery}${itemQuery ? "&" + itemQuery : ""}`,
      },
    });
  };

  // 跳地址列表
  const navigate2AddressList = query => {
    console.error("navigate2AddressList——", query);
    let h5Url = `${MICRO_APP_BASEURL}/#/addressList?shopId=${getUrlParam()?.["shopId"]}&actId=${
      getUrlParam()?.["actId"] || getUrlParam()?.["contentId"]
    }&addressOnlyId=${query?.addressOnlyId || getUrlParam()?.["addressOnlyId"]}&&fromPage=${
      query.fromPage
    }`;
    // let distributorId = getUrlParam()?.["distributorId"];
    // if(distributorId) {
    //   h5Url+=`&distributorId=${distributorId}`;
    // }
    if (query.awardQuantity) {
      h5Url += `&awardQuantity=${query.awardQuantity}`;
    }
    setTimeout(() => {
      navigate({
        h5Options: {
          url: h5Url,
        },
        type: undefined,
        replace: true,
        miniOptions: {
          url: "/mePkg/pages/address/address",
          query,
        },
      });
    }, 100);
  };

  // 跳指定的地址页
  const navigate2Address = (query: {
    shopId?: string;
    addressId: string;
    previous_page_name?: string;
    channel?: string;
    [key: string]: any;
  }) => {
    const shopId = query.shopId || getUrlParam()?.["shopId"];
    let h5Url = `${MICRO_APP_BASEURL}/#/address?shopId=${shopId}`;
    if (Object.keys(query).length > 0) {
      h5Url += `&${Object.keys(query)
        .map(key => `${key}=${query[key]}`)
        .join("&")}`;
    }

    setTimeout(() => {
      navigate({
        h5Options: {
          url: h5Url,
        },
        type: undefined,
        replace: false,
        // TODO
        miniOptions: {
          url: "/mePkg/pages/address/address-setting/address-setting",
          query: {
            from: "h5",
            ...query,
            shopId,
            id: query?.addressId || "",
          },
        },
      });
    });
  };

  // 小程序跳日历页面
  const navigate2SaleCalendar = query => {
    setTimeout(() => {
      navigate({
        miniOptions: {
          url: "/acts/pages/sale-calendar/sale-calendar",
          query,
        },
      });
    }, 100);
  };
  // 跳转订单详情
  const navigate2OrderDetail = query => {
    const pathQuery = `${location.href.split("?")[1]}`;
    navigate({
      appOptions: {
        url: `${process.env.REACT_APP_PAGE_URL_4_APP}/v2/order/detail?orderNo=${query.orderNo}&nh=1&${pathQuery}`,
      },
      h5Options: {
        url: `${MICRO_APP_BASEURL}/#/order/detail`,
        query: {
          orderNo: query.orderNo,
        },
      },
      miniOptions: {
        url: "/order/pages/order-detail-new/order-detail-new",
        query: {
          orderNo: query.orderNo,
        },
      },
      replace: query.replace,
    });
  };

  // 跳转到全部问答页面
  const navigate2QuestionsAnswers = (query: {
    h5Query?: {
      activitySpuId?: string;
      activityId?: string;
      selectSkuId?: string;
    };
  }) => {
    const url = `/mx-shop-micro/questions-answers?activitySpuId=${query.h5Query?.activitySpuId}&activityId=${query.h5Query?.activityId}&selectSkuId=${query.h5Query?.selectSkuId}&previous_page_name=${encodeURIComponent(document.title)}`;
    navigate({
      h5Options: {
        url: url,
      },
    });
  };
  // 跳转奖品订单详情
  const navigate2RewardDetail = query => {
    navigate({
      h5Options: {
        url: "/mx-shop-micro/reward-order",
        query,
      },
      miniOptions: {
        url: `${origin}/mx-shop-micro/reward-order`,
        query: query,
      },
      appOptions: {
        url: `${origin}/mx-shop-micro/reward-order?orderNo=${query.orderNo}`,
      },
    });
  };

  // 跳物流详情页面
  const navigate2Logistics = query => {
    navigate({
      h5Options: {
        url: "/mx-shop-micro/logistics",
        query,
      },
      miniOptions: {
        url: `${origin}/mx-shop-micro/logistics`,
        query: query,
      },
      appOptions: {
        url: `${origin}/mx-shop-micro/logistics?orderNo=${query.orderNo}&roleType=${query.roleType}`,
      },
    });
  };

  // 跳转全部榜单
  const navigate2RankAll = (query?: {
    [key in string]: any;
  }) => {
    navigate({
      h5Options: {
        url: "/mx-shop-micro/rank-all",
        query,
      },
      miniOptions: {
        url: `${origin}/mx-shop-micro/rank-all`,
        query: query,
      },
      appOptions: {
        url: `${origin}/mx-shop-micro/rank-all`,
        query: {
          ...query,
          nh: 1, // App 隐藏导航栏
        },
      },
    });
  };

  // 跳转榜单页
  const navigate2RankScale = query => {
    navigate({
      h5Options: {
        url: "/mx-shop-micro/rank-scale",
        query,
      },
      miniOptions: {
        url: `${origin}/mx-shop-micro/rank-scale`,
        query: query,
      },
      appOptions: {
        url: `${origin}/mx-shop-micro/rank-scale`,
        query: {
          ...query,
          nh: 1, // App 隐藏导航栏
        },
      },
    });
  };

  // RMS 跳转更多素材
  const nav2RmsMaterialList = (product: ProductListItemModel) => {
    const previous_page_name = `${encodeURIComponent(document.title)}`;
    const opts: any = {
      previous_page_name,
      spuId: product.spuId,
      baseProductId: product.spuId,
      activitySpuId: product.activitySpuId,
      useType: 1,
      queryType: 14,
    };
    /* eslint-disable */
    setTimeout(() => {
      navigate({
        appOptions: {
          url: "materialMore",
          query: {
            src: "PRODUCT",
            bizInfo: JSON.stringify({
              ...opts,
              pageId: "App_MaterialRecommend",
              titleFrontLocationCode: "App_MaterialRecommend_MaterialTitlefront",
            }),
          },
        },
        miniOptions: {
          url: "/material/pages/material-list-gray/material-list-gray",
          query: opts,
        },
        h5Options: {
          url: `/mx-shop-micro/material`,
          query: opts,
        },
      });
    }, 100);
  };

  return {
    nav2RewardMaterialList,
    nav2RewardMaterialContent,
    nav2RewardBrandMaterial,
    navigate2Home,
    navigate2Product,
    navigate2ProductVideoStream,
    navigate2MaterialVideoStream,
    navigate2Activity,
    navigate2LiveRoom,
    navigate2PreheatLiveRoom,
    navigate2LiveItem,
    navigate2LiveReview,
    navigate2OfferOrderList,
    navigate2GoodsOfNpieceXfold,
    navigate2Search,
    navigate2Category,
    navigate2Shop,
    navigate2Store,
    preasableAction,
    navigate2OrderConfrim,
    navigate2HiComi,
    navigate2SuperVenue,
    navigate2Venue,
    navigate,
    navigate2Cart,
    navigate2SaasCoupon,
    navigate2CouponList,
    navigate2H5,
    navigate2BrandDetail,
    navigate2BrandStory,
    navigate2MaterialContent,
    navigate2MaterialList,
    navigate2BrandMaterialList,
    navigate2CBrandStory,
    navigate2SearchPage,
    navigate2BusinessLicense,
    navigate2NxProductList,
    navigate2Material,
    navigate2Live,
    navigate2NewPersonPage,
    navigate2AddressList,
    navigate2Address,
    navigate2SaleCalendar,
    navigate2MyItem,
    navigate2OrderDetail,
    navigate2RewardDetail,
    navigate2Logistics,
    navigateBack,
    navigate2RankAll,
    navigate2RankScale,
    navigate2QuestionsAnswers,
    nav2RmsMaterialList,
  };
}

export default useNavigation;
